import { createRootRoute, createRoute, createRouter, redirect, retainSearchParams } from '@tanstack/react-router'
import { lazy } from 'react'
import App from './App'
import type { FilterModel } from './generated/graphql'
import NotFoundPage from './pages/NotFoundPage'
import PaymentDetailsPage from './pages/PaymentDetails'
import PaymentsPage from './pages/Payments/Payments'

const AddLedgerItemsPage = lazy(() => import('./pages/AddLedgerItems'))
const DriverPaymentsPage = lazy(() => import('./pages/DriverPayments/DriverPayments'))

interface SortOrder {
  field: string
  sort: 'asc' | 'desc'
}

interface PaymentsSearch {
  page?: number
  pageSize?: number
  sort?: SortOrder[]
  filter?: FilterModel[]
}

const rootRoute = createRootRoute({
  component: App,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async () => {
    throw redirect({
      to: '/payments/status/open',
    })
  },
})

const paymentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payments/status/$status',
  component: PaymentsPage,
  validateSearch: (search: Record<string, unknown>): PaymentsSearch => {
    return {
      page: typeof search.page === 'number' ? search.page : undefined,
      pageSize: typeof search.pageSize === 'number' ? search.pageSize : undefined,
    }
  },
  search: {
    middlewares: [retainSearchParams(['pageSize', 'sort', 'filter'])],
  },
})

type PaymentDetailsTab = 'details' | 'history'

interface PaymentDetailsSearch {
  tab?: PaymentDetailsTab
}

const paymentDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payments/$paymentId',
  component: PaymentDetailsPage,
  validateSearch: (search: Record<string, unknown>): PaymentDetailsSearch => {
    const tab = search.tab
    return {
      tab: tab === 'details' || tab === 'history' ? tab : undefined,
    }
  },
})

const addLedgerItemsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/add-ledger-items',
  component: () => <AddLedgerItemsPage />,
})

const driverPaymentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/driver-payments',
  component: DriverPaymentsPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      driverId: typeof search.driverId === 'string' ? search.driverId : undefined,
      page: typeof search.page === 'number' ? search.page : undefined,
      pageSize: typeof search.pageSize === 'number' ? search.pageSize : undefined,
    }
  },
})

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: NotFoundPage,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  paymentsRoute,
  paymentDetailsRoute,
  addLedgerItemsRoute,
  driverPaymentsRoute,
  notFoundRoute,
])

export const router = createRouter({ routeTree })
